import React, {
  useState,
  useContext,
  createContext,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import { config } from "../config";
import { analytics } from "./firebase";
import { logEvent } from "firebase/analytics";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  useEffect(() => {
    document.documentElement.style.setProperty("--main-color", config.color);
  }, [config.color]);
  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await fetch(`${config.baseURL}/validate_token.php`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) {
          setUser(null);
          return;
        }
        const { user: u } = await res.json();
        setUser(u);
      } catch (err) {
        
        setUser(null);
      }
    };

    loadUser();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const GoogleAuth = ({ onLoginSuccess, id, showDropdown = false }, ref) => {
  const { user, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const login = useGoogleLogin({
    onSuccess: (resp) => {
      setLoading(false);
      handleLoginSuccess(resp.access_token);
    },
    onError: () => {
      setLoading(false);
      toast.error("Google login failed");
    },
    scope: "openid profile email",
    responseType: "token",
  });

  useImperativeHandle(
    ref,
    () => ({
      login: () => {
        if (!loading) {
          setLoading(true);
          login();
        }
      },
    }),
    [loading]
  );

  const handleLoginSuccess = async (googleToken) => {
    try {
      const profileRes = await fetch(
        "https://www.googleapis.com/oauth2/v2/userinfo",
        {
          headers: { Authorization: `Bearer ${googleToken}` },
        }
      );
      if (!profileRes.ok) throw new Error("Google profile fetch failed");
      const {
        email,
        name,
        id: social_id,
        picture,
        verified_email,
      } = await profileRes.json();

      const loginRes = await fetch(`${config.baseURL}/login.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name,
          social_id,
          profile_picture: picture,
          is_email_verified: verified_email,
        }),
      });
      if (!loginRes.ok) throw new Error("Backend auth failed");

      const data = await loginRes.json();
      const userObj = {
        id: data.user_id,
        email: data.email,
        name: data.name,
        picture: data.picture,
      };

      setUser(userObj);
      onLoginSuccess?.(userObj);
      logEvent(analytics, "login", { method: "Google", email: data.email });

      if (localStorage.getItem("navigateToDashboard") === "true") {
        navigate("/dashboard");
        localStorage.removeItem("navigateToDashboard");
      }
    } catch (err) {
      
      toast.error(err.message || "Login encountered an error");
      setLoading(false);
    }
  };
  const handleLogout = async () => {
    try {
      const res = await fetch(`${config.baseURL}/logout.php`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Logout failed");

      setUser(null);
      logEvent(analytics, "logout", { method: "Google" });
          window.location.reload();
    } catch (err) {
     
      toast.error(err.message || "Logout failed");
    }
  };

  const handleClickLogin = () => {
    if (loading) return;
    setLoading(true);
  
    // open the Google popup
    login();
  
    // when this window regains focus, clear the loading state
    const handleFocus = () => {
      setLoading(false);
      window.removeEventListener("focus", handleFocus);
    };
    window.addEventListener("focus", handleFocus);
  };

  return (
    <div className="google-auth-container">
      {user ? (
        showDropdown ? (
          <div className="dropdown-wrapper">
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="dropdown-btn"
              id={id}
            >
              <img
                src={user.picture}
                alt={user.name}
                className="user-picture"
              />
              {user.name}
              <span className="dropdown-icon" title="Logout">
                ▼
              </span>
            </button>
            {dropdownOpen && (
              <div className="dropdown-menu">
                <div
                  onClick={handleLogout}
                  className="logout-item"
                  title="Logout"
                  style={{ padding: "8px 12px", cursor: "pointer" }}
                >
                  Logout
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={handleLogout}
            className="logout-btn"
            id={id}
            title="Logout"
          >
            Logout
          </button>
        )
      ) : (
        <button
         /*  onClick={() => {
            setLoading(true);
            login();
          }} */
            onClick={handleClickLogin}
         
          className="login-btn"
          title="Login"
          id={id}
          disabled={loading}
        >
          {loading ? "Logging" : "Login"}
        </button>
      )}
    </div>
  );
};

export default forwardRef(GoogleAuth);
